import React, { useState, useRef, useCallback } from 'react';
import { DownloadButton } from './DownloadButton';
import { DownloadAdvancedModal } from './DownloadAdvancedModal';
import type { DownloadAdvancedOptions } from './DownloadAdvancedModal';
import { useDownloadManagerContext } from '../contexts/DownloadManagerContext';
import { useAuth } from '../contexts/AuthContext';

export interface RecordingCardProps {
  cameraId: string;
  cameraName: string;
  /** Site ID (número) */
  siteId?: number;
  /** Camera ID numérico (devices_cameras.id) */
  cameraIdNum?: number;
  /** Fecha/hora de la grabación o del segmento actual */
  startTime: Date;
  /** Fin del rango de playback preparado (endTime del slot HLS). */
  playbackEndTime?: Date;
  /** Duración total de la grabación (ej. "2h 15min") */
  fullDuration: string;
  /** Duración del segmento actual (ej. "5 min") */
  segmentDuration: string;
  /** Tamaño estimado video completo (ej. "~850 MB") */
  fullSizeEstimate?: string;
  /** Tamaño estimado segmento (ej. "~12 MB") */
  segmentSizeEstimate?: string;
  /** Si true, deshabilitar "Video completo" */
  isLive?: boolean;
  canDownload?: boolean;
  /** requestId activo del slot (sesión HLS preparada). Habilita el fast-path en video-engine. */
  playbackRequestId?: string;
  className?: string;
  children: React.ReactNode;
}

export const RecordingCard: React.FC<RecordingCardProps> = ({
  cameraId,
  cameraName,
  siteId,
  cameraIdNum,
  startTime,
  canDownload = true,
  playbackRequestId,
  className = '',
  children,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const { addDownload } = useDownloadManagerContext();

  const { user } = useAuth();
  const recordingMaxHoursBack = user?.recordingMaxHoursBack ?? null;
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Límites del selector calculados al abrir el modal (así "ahora" no queda viejo).
  const [downloadBounds, setDownloadBounds] = useState<{ min?: Date; max: Date }>(() => ({ max: new Date() }));

  const openDownloadModal = useCallback(() => {
    const now = new Date();
    setDownloadBounds({
      // Restricción de perfil: no se descarga lo que no se puede ver.
      min: recordingMaxHoursBack != null ? new Date(now.getTime() - recordingMaxHoursBack * 3600_000) : undefined,
      max: now,
    });
    setAdvancedOpen(true);
  }, [recordingMaxHoursBack]);

  const defaultDownloadStart =
    downloadBounds.min && startTime.getTime() < downloadBounds.min.getTime() ? downloadBounds.min : startTime;

  const handleAdvancedConfirm = useCallback(
    (options: DownloadAdvancedOptions) => {
      addDownload({
        type: 'advanced',
        cameraId,
        cameraName,
        siteId,
        cameraIdNum,
        startTime: options.startTime,
        endTime: options.endTime,
        quality: 'original',
        format: 'mp4',
        includeAudio: true,
        includeWatermark: false,
        packaging: 'mp4',
        // Si el rango elegido no está en la sesión HLS preparada, video-engine cae a NFS.
        requestId: playbackRequestId,
      });
      setAdvancedOpen(false);
    },
    [addDownload, cameraId, cameraName, siteId, cameraIdNum, playbackRequestId]
  );

  // El usuario elige la fecha/hora de inicio y la duración (5 / 10 min) en el modal.
  return (
    <div className={`recording-card ${className}`}>
      {children}
      <div ref={buttonRef}>
        <DownloadButton
          ariaLabel={`Descargar grabación de ${cameraName}`}
          onClick={openDownloadModal}
          disabled={!canDownload}
        />
      </div>
      <DownloadAdvancedModal
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        cameraName={cameraName}
        defaultStart={defaultDownloadStart}
        defaultDurationMinutes={5}
        minDate={downloadBounds.min}
        maxDate={downloadBounds.max}
        onConfirm={handleAdvancedConfirm}
      />
    </div>
  );
};
